/**
 * contexts/DataContext.tsx
 * ------------------------------------------------------------------
 * One typed, app-wide live data layer. Subscribes to every core
 * collection once and shares it, so feature modules never wire their
 * own listeners. Collection names mirror v1 (report* prefix) for an
 * easy data migration path; `users` stays unprefixed.
 *
 * SCOPING (H3): for a PARENT-ONLY account, the sensitive roster
 * collections are scoped to that parent's own data — `students` is
 * narrowed to their linked children and `families` to their own family
 * — so a parent never streams the whole school's roster. Catalog/config
 * collections (teachers, subjects, classes, term status) stay shared as
 * they carry no per-child PII. Staff accounts are unchanged. Per-child
 * marks / attendance / discipline / reports are NOT loaded here for
 * parents at all — those come from the getChildAcademics callable.
 * ------------------------------------------------------------------
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  documentId,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { sortClasses } from '@/lib/classSort';
import { useAuth } from '@/contexts/AuthContext';
import type {
  Teacher,
  Student,
  Subject,
  SchoolClass,
  AppUser,
  TermStatusDocument,
  AssessmentConfigDocument,
  ExamTitleDocument,
  ReportNoticeDocument,
  Family,
  TermCalendarDocument,
} from '@/types';

/** Firestore collection names (v1-compatible). */
export const COLLECTIONS = {
  teachers: 'reportTeachers',
  /** Names only (title, initials, surname, full and display name), kept in
   *  step by the onTeacherWrittenNames trigger. What a PARENT reads instead
   *  of reportTeachers, which carries phone numbers, staff IDs and notes. */
  teacherNames: 'reportTeacherNames',
  students: 'reportStudents',
  subjects: 'reportSubjects',
  classes: 'reportClasses',
  users: 'users',
  marks: 'reportMarks',
  attendance: 'reportAttendance',
  reportComments: 'reportComments',
  commentTemplates: 'reportCommentTemplates',
  flags: 'reportFlags',
  termStatus: 'reportTermStatus',
  assessmentConfig: 'reportAssessmentConfig',
  examTitles: 'reportExamTitles',
  reportNotices: 'reportNotices',
  generatedReports: 'reportGenerated',
  reportBatches: 'reportBatches',
  notifications: 'reportNotifications',
  auditLog: 'reportAuditLog',
  discipline: 'reportDiscipline',
  achievements: 'reportAchievements',
  dailyAttendance: 'reportDailyAttendance',
  enrollments: 'reportEnrollments',
  promotionRules: 'reportPromotionRules',
  parentInvitations: 'reportParentInvitations',
  schoolSettings: 'reportSchoolSettings',
  announcements: 'reportAnnouncements',
  curriculum: 'reportCurriculum',
  applications: 'reportApplications',
  families: 'reportFamilies',
  examSessions: 'examSessions',
  /* Teaching + study, ported from Al-Nur (TEACHING-AND-STUDENT-PORTAL-PLAN.md).
     Same names as Al-Nur's so the synced engine in lib/study runs unchanged. */
  studyItems: 'reportStudyItems',
  studyAttempts: 'reportStudyAttempts',
  studyMastery: 'reportStudyMastery',
  studyCards: 'reportStudyCards',
  studyCardProgress: 'reportStudyCardProgress',
  studyNotes: 'reportStudyNotes',
  studyAssignments: 'reportStudyAssignments',
  /* Which set texts a class studies (lib/study/textChoice), per syllabus and class. */
  syllabusChoices: 'reportSyllabusChoices',
  /** Interactive lessons: which sections a pupil has unlocked. Client-written
   *  by the pupil, like card progress, because it grades nothing. */
  lessonProgress: 'reportLessonProgress',
} as const;

interface DataContextValue {
  teachers: Teacher[];
  students: Student[];
  /** Students in non-archived classes — the active roster. Use this for
   *  compliance / attendance / analytics so alumni (archived classes) are
   *  excluded. `students` remains the full unfiltered set. */
  activeStudents: Student[];
  subjects: Subject[];
  classes: SchoolClass[];
  users: AppUser[];
  termStatuses: TermStatusDocument[];
  assessmentConfigs: AssessmentConfigDocument[];
  examTitles: ExamTitleDocument[];
  reportNotices: ReportNoticeDocument[];
  families: Family[];
  /** Admin-editable term dates + reopen dates (null until loaded / unset). */
  termCalendar: TermCalendarDocument | null;
  loading: boolean;
  error: string | null;
  /** Deployed build id from appConfig/version (null until resolved / signed out). */
  serverBuild: string | null;
  /** Convenience lookups */
  teacherById: (id: string) => Teacher | undefined;
  studentById: (id: string) => Student | undefined;
  subjectById: (id: string) => Subject | undefined;
  classById: (id: string) => SchoolClass | undefined;
  familyById: (id: string) => Family | undefined;
}

const DataContext = createContext<DataContextValue | undefined>(undefined);

export function useData(): DataContextValue {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within <DataProvider>');
  return ctx;
}

function withId<T>(snapDocs: { id: string; data: () => unknown }[]): T[] {
  return snapDocs.map((d) => ({ id: d.id, ...(d.data() as object) })) as T[];
}

export function DataProvider({ children }: { children: ReactNode }) {
  const { currentUser, userData } = useAuth();

  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [termStatuses, setTermStatuses] = useState<TermStatusDocument[]>([]);
  const [serverBuild, setServerBuild] = useState<string | null>(null);
  const [assessmentConfigs, setAssessmentConfigs] = useState<
    AssessmentConfigDocument[]
  >([]);
  const [examTitles, setExamTitles] = useState<ExamTitleDocument[]>([]);
  const [reportNotices, setReportNotices] = useState<
    ReportNoticeDocument[]
  >([]);
  const [families, setFamilies] = useState<Family[]>([]);
  const [termCalendar, setTermCalendar] = useState<TermCalendarDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Parent-only accounts get a scoped view (their children + own family).
  const roles: string[] =
    userData?.roles && userData.roles.length
      ? (userData.roles as string[])
      : userData?.role
        ? [userData.role as string]
        : [];
  const parentScoped = roles.includes('parent') && !roles.some((r) => r !== 'parent');
  /* A HOUSEMASTER is scoped the same way a parent is, and for the same
     reason: their job covers a named set of children, not the school. They
     are deliberately NOT in isAnyStaff, so an unscoped roster query would be
     denied outright — and widening isAnyStaff to admit them would hand them
     all 826 pupils, which is the thing the school asked us to avoid. */
  const boardingScoped =
    roles.includes('boardingMaster') && !roles.some((r) => r !== 'boardingMaster');
  /* MEDIA reads pupils' names, classes and photos and nothing else (rules:
     reportStudents read, studentPhotos read). Loading families or report
     notices would only fail, and show an error. */
  const mediaScoped = roles.includes('media') && !roles.some((r) => r !== 'media');
  /* A STUDENT-only account reads none of the core collections (from Al-Nur,
     Oct 2026). Everything their portal needs is static syllabus content, their
     own work, or comes from their own callables (getMyAcademics), so nothing
     is subscribed — subscribing anyway would only collect permission errors
     once the isInternalUser() sweep lands. */
  const studentScoped = roles.includes('student') && !roles.some((r) => r !== 'student');
  const myHouses: string[] = boardingScoped
    ? ((userData?.boardingHouses as string[] | undefined) ?? [])
    : [];
  const houseKey = myHouses.join(',');
  // The full `users` collection query is only authorised for admin/head by
  // the Firestore rules (isAdminOrHead); every other signed-in user may read
  // only their own user doc, which AuthContext already loads. Non-admin/head
  // staff therefore skip the directory subscription instead of tripping a
  // permission-denied on it.
  const ADMIN_OR_HEAD = ['admin', 'systemAdmin', 'academicAdmin', 'headteacher'];
  const adminOrHead = roles.some((r) => ADMIN_OR_HEAD.includes(r));
  const childIds = (userData?.parentAccount?.studentIds ?? []).slice(0, 30);
  const childKey = childIds.join(',');

  useEffect(() => {
    if (!currentUser) {
      setTeachers([]);
      setStudents([]);
      setSubjects([]);
      setClasses([]);
      setUsers([]);
      setTermStatuses([]);
      setServerBuild(null);
      setAssessmentConfigs([]);
      setExamTitles([]);
      setReportNotices([]);
      setFamilies([]);
      setTermCalendar(null);
      setLoading(false);
      return;
    }

    if (studentScoped) {
      setTeachers([]);
      setStudents([]);
      setSubjects([]);
      setClasses([]);
      setUsers([]);
      setTermStatuses([]);
      setServerBuild(null);
      setAssessmentConfigs([]);
      setExamTitles([]);
      setReportNotices([]);
      setFamilies([]);
      setTermCalendar(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const ready = {
      teachers: false,
      students: false,
      subjects: false,
      classes: false,
    };
    const markReady = (k: keyof typeof ready) => {
      ready[k] = true;
      if (Object.values(ready).every(Boolean)) setLoading(false);
    };
    const onErr = (label: string) => (e: Error) => {
      console.error(`[data] ${label} subscription error:`, e);
      setError(`Failed to load ${label}.`);
    };

    // Catalog/config collections — shared for everyone (no per-child PII).
    const unsubs = [
      /* A parent gets teachers' NAMES and nothing else (security group B,
         30 Oct 2026): the portal only labels each subject with its teacher.
         The rest of a teacher record is staff business. */
      onSnapshot(
        query(collection(db, parentScoped ? COLLECTIONS.teacherNames : COLLECTIONS.teachers)),
        (s) => {
          setTeachers(withId<Teacher>(s.docs));
          markReady('teachers');
        },
        onErr('teachers'),
      ),
      onSnapshot(
        query(collection(db, COLLECTIONS.subjects)),
        (s) => {
          setSubjects(withId<Subject>(s.docs));
          markReady('subjects');
        },
        onErr('subjects'),
      ),
      onSnapshot(
        query(collection(db, COLLECTIONS.classes)),
        (s) => {
          // Canonical order at the source: Form 1→5 → AS → A2, streams
          // alphabetical. Every dropdown that maps `classes` inherits it.
          setClasses(sortClasses(withId<SchoolClass>(s.docs)));
          markReady('classes');
        },
        onErr('classes'),
      ),
      onSnapshot(
        query(collection(db, COLLECTIONS.termStatus)),
        (s) => setTermStatuses(withId<TermStatusDocument>(s.docs)),
        onErr('term status'),
      ),
      // Version gate: the single appConfig/version doc drives stale-client
      // detection. Rides this shared channel so every signed-in client
      // (staff and parent) watches it with no extra infrastructure.
      onSnapshot(
        doc(db, 'appConfig', 'version'),
        (s) =>
          setServerBuild(
            s.exists() ? ((s.data() as { build?: string }).build ?? null) : null,
          ),
        onErr('app version'),
      ),
      // Term calendar (dates + reopen dates) — shared, read by report cards.
      onSnapshot(
        doc(db, COLLECTIONS.schoolSettings, 'termCalendar'),
        (s) => setTermCalendar(s.exists() ? ({ id: s.id, ...(s.data() as object) } as TermCalendarDocument) : null),
        onErr('term calendar'),
      ),
    ];

    if (mediaScoped) {
      unsubs.push(
        onSnapshot(
          query(collection(db, COLLECTIONS.students)),
          (s) => {
            setStudents(withId<Student>(s.docs));
            markReady('students');
          },
          onErr('students'),
        ),
      );
      setFamilies([]);
      setExamTitles([]);
      setReportNotices([]);
      setUsers([]);
    } else if (boardingScoped) {
      /* STUDENTS — only the boarders in this housemaster's house(s).
         An empty house list subscribes to nothing: no houses means no
         boarders, never "all of them". */
      if (myHouses.length === 0) {
        setStudents([]);
        markReady('students');
      } else {
        unsubs.push(
          onSnapshot(
            query(
              collection(db, COLLECTIONS.students),
              where('admissions.boardingHouse', 'in', myHouses.slice(0, 10)),
            ),
            (s) => {
              // The query cannot also filter boardingStatus without a
              // composite index, so the status check lands here. A pupil
              // moved back to day scholar keeps their old house field.
              setStudents(
                withId<Student>(s.docs).filter(
                  (st) => st.admissions?.boardingStatus === 'boarding',
                ),
              );
              markReady('students');
            },
            onErr('students'),
          ),
        );
      }
      setFamilies([]);
      setExamTitles([]);
      setReportNotices([]);
    } else if (parentScoped) {
      // STUDENTS — only the parent's linked children.
      if (childIds.length === 0) {
        setStudents([]);
        markReady('students');
      } else {
        unsubs.push(
          onSnapshot(
            query(collection(db, COLLECTIONS.students), where(documentId(), 'in', childIds)),
            (s) => {
              setStudents(withId<Student>(s.docs));
              markReady('students');
            },
            onErr('students'),
          ),
        );
      }
      // FAMILIES — only the family/families containing the parent's children.
      if (childIds.length === 0) {
        setFamilies([]);
      } else {
        unsubs.push(
          onSnapshot(
            query(collection(db, COLLECTIONS.families), where('studentIds', 'array-contains-any', childIds)),
            (s) => setFamilies(withId<Family>(s.docs)),
            onErr('families'),
          ),
        );
      }
      // Parents do not need these — keep them empty (and avoid the
      // permission-denied the full `users` query would throw).
      setUsers([]);
      setAssessmentConfigs([]);
      setExamTitles([]);
      setReportNotices([]);
    } else {
      // STAFF — full roster + config.
      unsubs.push(
        onSnapshot(
          query(collection(db, COLLECTIONS.students)),
          (s) => {
            setStudents(withId<Student>(s.docs));
            markReady('students');
          },
          onErr('students'),
        ),
        onSnapshot(
          query(collection(db, COLLECTIONS.assessmentConfig)),
          (s) => setAssessmentConfigs(withId<AssessmentConfigDocument>(s.docs)),
          onErr('assessment config'),
        ),
        onSnapshot(
          query(collection(db, COLLECTIONS.examTitles)),
          (s) => setExamTitles(withId<ExamTitleDocument>(s.docs)),
          onErr('exam titles'),
        ),
        onSnapshot(
          query(collection(db, COLLECTIONS.reportNotices)),
          (s) => setReportNotices(withId<ReportNoticeDocument>(s.docs)),
          onErr('report notices'),
        ),
        onSnapshot(
          query(collection(db, COLLECTIONS.families)),
          (s) => setFamilies(withId<Family>(s.docs)),
          onErr('families'),
        ),
      );

      // USERS directory — admin/head only (see adminOrHead note above).
      if (adminOrHead) {
        unsubs.push(
          onSnapshot(
            query(collection(db, COLLECTIONS.users)),
            (s) => setUsers(withId<AppUser>(s.docs)),
            onErr('users'),
          ),
        );
      } else {
        setUsers([]);
      }
    }

    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, parentScoped, boardingScoped, mediaScoped, studentScoped, houseKey, adminOrHead, childKey]);

  const value = useMemo<DataContextValue>(
    () => {
    // Active roster = current students only. Excludes those marked 'left'
    // (graduated/withdrawn/transferred), those still 'pending' (in Pastel but
    // not yet enrolled — the Admissions Pending tab), and anyone parked in an
    // archived class (the interim Alumni mechanism). Full `students` stays
    // unfiltered.
    const archivedClassIds = new Set(classes.filter((c) => c.archived).map((c) => c.id));
    const activeStudents = students.filter(
      (s) => s.status !== 'left' && s.status !== 'pending' && !archivedClassIds.has(s.currentClass),
    );
    return {
      teachers,
      students,
      activeStudents,
      subjects,
      classes,
      users,
      termStatuses,
      assessmentConfigs,
      examTitles,
      reportNotices,
      families,
      termCalendar,
      loading,
      error,
      serverBuild,
      teacherById: (id) => teachers.find((t) => t.id === id),
      studentById: (id) => students.find((s) => s.id === id),
      subjectById: (id) => subjects.find((s) => s.id === id),
      classById: (id) => classes.find((c) => c.id === id),
      familyById: (id) => families.find((f) => f.id === id),
    };
    },
    [teachers, students, subjects, classes, users, termStatuses, assessmentConfigs, examTitles, reportNotices, families, termCalendar, loading, error, serverBuild],
  );

  return (
    <DataContext.Provider value={value}>{children}</DataContext.Provider>
  );
}
